import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { useSelector } from "react-redux";
import { getVesselEmissionFuelConsumption } from "../../../services/vesselEmissionService";

const FuelConsChart = ({ voyageId }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [fuelData, setFuelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFuelConsumption = async () => {
      if (!currentVessel?._id) {
        console.log("No vessel selected, skipping fetch");
        return;
      }

      if (!voyageId && (!startDate || !endDate)) {
        console.log(
          "No voyage selected and missing date range, skipping fetch"
        );
        return;
      }

      setLoading(true);
      try {
        const response = await getVesselEmissionFuelConsumption(
          currentVessel._id,
          voyageId ? null : startDate,
          voyageId ? null : endDate,
          voyageId
        );

        console.log("response", response);

        if (response?.status === "error") {
          setError("Failed to fetch fuel consumption data");
          setFuelData(null);
        } else {
          if (response) {
            setFuelData(response);
          } else {
            setFuelData(null);
          }
          setError(null);
        }
      } catch (err) {
        setError(err.message);
        console.error("API Error:", err);
        setFuelData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFuelConsumption();
  }, [currentVessel, startDate, endDate, voyageId]);

  // Process the data to calculate total consumption by engine type
  const calculateEngineConsumption = (data) => {
    if (!data?.percentagesByEngine) return {};

    return {
      "Main Engine": data.percentagesByEngine.mainEngine?.percentage || 0,
      "Aux Engine": data.percentagesByEngine.auxEngine?.percentage || 0,
      Boiler: data.percentagesByEngine.boiler?.percentage || 0,
      Other: data.percentagesByEngine.others?.percentage || 0,
    };
  };

  const engineConsumption = calculateEngineConsumption(fuelData);

  const colors = [
    "#1a5276", // CO2 color (dark blue)
    "#27ae60", // SOx color (green)
    "#d35400", // NOx color (orange)
    "#7d3c98", // PM color (purple)
  ];

  const [selectedEngine, setSelectedEngine] = useState("All Engines");

  const total = Object.values(engineConsumption).reduce(
    (sum, val) => sum + val,
    0
  );

  const pieData =
    selectedEngine === "All Engines"
      ? Object.entries(engineConsumption).map(([name, value], index) => ({
          name,
          y: value,
          color: colors[index % colors.length],
          borderColor: "#ffffff",
          borderWidth: 1,
        }))
      : [
          {
            name: selectedEngine,
            y: engineConsumption[selectedEngine],
            color:
              colors[
                Object.keys(engineConsumption).indexOf(selectedEngine) %
                  colors.length
              ],
            borderColor: "#ffffff",
            borderWidth: 1,
          },
          {
            name: "Other Engines",
            y: total - engineConsumption[selectedEngine],
            color: "#f1f5f9",
            borderColor: "#e2e8f0",
            borderWidth: 1,
          },
        ];

  const options = {
    chart: {
      type: "pie",
      height: 550,
      backgroundColor: "transparent",
    },
    title: {
      text: null,
    },
    tooltip: {
      pointFormat: "<b>{point.name}</b>: {point.y:.2f}%",
      backgroundColor: "#ffffff",
      borderColor: "#e2e8f0",
      borderRadius: 6,
      style: {
        color: "#1a202c",
        fontSize: "14px",
      },
    },
    plotOptions: {
      pie: {
        borderWidth: 1,
        borderColor: "#ffffff",
        allowPointSelect: true,
        cursor: "pointer",
        size: "70%",
        center: ["50%", "50%"],
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b><br/>{point.percentage:.2f}%",
          distance: 25,
          softConnector: false,
          connectorWidth: 1,
          connectorColor: "#94a3b8",
          connectorPadding: 3,
          style: {
            color: "#1f2937",
            fontSize: "10px",
            fontWeight: "500",
            textOutline: "none",
          },
          useHTML: false,
          overflow: "allow",
          crop: false,
          padding: 1,
        },
        showInLegend: true,
      },
    },
    series: [
      {
        name: "Consumption",
        colorByPoint: true,
        data: pieData,
      },
    ],
    credits: {
      enabled: false,
    },
  };

  if (loading) {
    return (
      <div className="mx-auto">
        <h2 className="text-lg font-semibold text-gray-800 tracking-tight mb-4">
          Fuel Consumption by Consumer
        </h2>
        <div className="bg-white/50 backdrop-blur border border-[#d7e9f7] p-8 shadow-[0_8px_20px_rgba(0,0,0,0.05)] rounded-lg text-center">
          Loading fuel consumption data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto">
        <h2 className="text-lg font-semibold text-gray-800 tracking-tight mb-4">
          Fuel Consumption by Consumer
        </h2>
        <div className="bg-white/50 backdrop-blur border border-[#d7e9f7] p-8 shadow-[0_8px_20px_rgba(0,0,0,0.05)] rounded-lg text-center text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      <div className="flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center rounded-tl-2xl rounded-tr-2xl">
          <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
            Fuel Consumption by Consumer
          </h2>
        </div>

        {/* Filter Section */}
        {fuelData && (
          <div className="flex justify-end gap-4 px-6 mt-4">
            <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
              Select by:
            </label>
            <div className="relative">
              <select
                value={selectedEngine}
                onChange={(e) => setSelectedEngine(e.target.value)}
                className="w-full min-w-[180px] pl-4 pr-10 py-2.5 rounded-full bg-white border border-[#d7e9f7] text-gray-700 text-sm font-medium appearance-none hover:border-[#27ade3] focus:outline-none focus:ring-2 focus:ring-[#27ade3]/50 focus:border-transparent transition-all duration-200 shadow-sm"
              >
                <option value="All Engines" className="text-sm">
                  All
                </option>
                {Object.keys(engineConsumption).map((engine) => (
                  <option key={engine} value={engine} className="text-sm">
                    {engine}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#27ade3]">
                <ChevronDown className="w-4 h-4" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="">
        {fuelData ? (
          <HighchartsReact highcharts={Highcharts} options={options} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            No fuel consumption data available
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelConsChart;
